import type { HelpStep } from "newui/components/ui/Help";
import {
	TOUR_USER_DEACTIVATED,
	TOUR_USER_INVITED,
} from "./tourFixtures";

/**
 * Guided tour steps for the User Management page.
 *
 * Flow:
 *   1. Highlight "Invite User" → open the invite drawer.
 *   2. Walk through the invite form.
 *   3. Highlight "Send invite" → close the drawer.
 *   4. Point at the user list.
 *   5–7. Use injected dummy rows to demonstrate resend / deactivate / reactivate.
 *
 * Steps that depend on the drawer being open drive the UI via
 * `onBeforeStep` / `onAfterStep`; `useGuidedTour` waits for each target
 * to be laid out before advancing Joyride.
 */
export interface UserListSteps {
	closeInviteDrawer: () => void;
	openInviteDrawer: () => void;
}

export const buildUserListSteps = ({
	openInviteDrawer,
	closeInviteDrawer,
}: UserListSteps): HelpStep[] => [
		{
			target: '[data-testid="invite-user-button"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Invite a new user</h3>
					<p>
						Start here to invite a teammate to your company. We&apos;ll open the
						invite drawer for you on the next step.
					</p>
				</div>
			),
			placement: "bottom",
			disableBeacon: true,
			onAfterStep: () => {
				openInviteDrawer();
			},
		},
		{
			target: '[data-testid="invite-user-form"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Fill in the details</h3>
					<p>
						Enter the user&apos;s name, email and role. Their email domain must
						match your company — we&apos;ll warn you if it doesn&apos;t.
					</p>
				</div>
			),
			placement: "left",
			disableBeacon: true,
			spotlightPadding: 280,
		},
		{
			target: '[data-testid="invite-submit-button"]',
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Send the invite</h3>
					<p>
						Click here to send the invite email. The user will appear in the
						table with a status of &apos;invited&apos;.
					</p>
				</div>
			),
			placement: "top",
			disableBeacon: true,
			onAfterStep: () => {
				closeInviteDrawer();
			},
		},
		{
			target: `[data-testid="user-action-resend-${TOUR_USER_INVITED.id}"]`,
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Resend an invite</h3>
					<p>
						If a user&apos;s invite expires (after 72 hours) or they&apos;ve lost
						the email, click the mail icon to send a fresh invite link.
					</p>
				</div>
			),
			placement: "left",
			disableBeacon: true,
		},
		{
			target: `[data-testid="user-action-deactivate-${TOUR_USER_INVITED.id}"]`,
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Deactivate a user</h3>
					<p>
						Click the red icon to revoke a user&apos;s access. They&apos;ll no
						longer be able to log in, but their account is preserved so you can
						reactivate them later.
					</p>
				</div>
			),
			placement: "left",
			disableBeacon: true,
		},
		{
			target: `[data-testid="user-action-reactivate-${TOUR_USER_DEACTIVATED.id}"]`,
			content: (
				<div className="text-sm">
					<h3 className="mb-2 text-base font-semibold">Reactivate a user</h3>
					<p>
						Deactivated users show a reactivate icon. Click it to send them a
						fresh invite — once they finish registration they&apos;ll regain
						access.
					</p>
				</div>
			),
			placement: "left",
			disableBeacon: true,
		},
	];
